import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { Grid, Button } from '@mui/material';
import { BOOKING } from '../../../../../reusable-components/datagrid/vendorColumns';
import BookingAccountForm from './BookingAccountForm';
import { ConfirmDialog } from '../../../../../reusable-components/confirm-dialog';
import { enqueueSnackbar } from '../../../../../reusable-components/snackbar';
import CustomDataGrid from '../../../../../reusable-components/datagrid/CustomDataGrid';
import Iconify from '../../../../../reusable-components/iconify';
import EditIconButton from '../../../../../reusable-components/basic-buttons/EditIconButton';
import axiosInstance from '../../../../../utils/axios';
import { useSelector } from '../../../../../redux/store';

BookingAccountTable.propTypes = {
  selectedRow: PropTypes.object,
};

export default function BookingAccountTable({ selectedRow }) {
  const { data: { vendors } } = useSelector((state) => state.vendors);

  const [accounts, setAccounts] = useState([]);
  const [bookingVendors, setBookingVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editAccount, setEditAccount] = useState(null);

  useEffect(() => {
    if (vendors && vendors.length > 0) {
      setIsLoading(true);
      axiosInstance.get(`Vendor/VendorBookingAccounts?vendorId=${selectedRow?.id}`)
        .then(res => {
          const bookVendorHash = res.data.bookingVendors.reduce((a, b) => { a[b.value] = b.label; return a; }, {})
          const vendorHash = vendors.reduce((a, b) => { a[b.id] = b.name; return a; }, {})
          setBookingVendors(res.data.bookingVendors);
          setAccounts(res.data.accounts.map(account => ({ ...account, bookingVendor: bookVendorHash[account.bookingVendorId] ?? "", vendor: vendorHash[account.vendorId] ?? "" })));
        })
        .catch(() => enqueueSnackbar('Error fetching booking data', { variant: 'error' }))
        .finally(() => setIsLoading(false));
    }
  }, [selectedRow, vendors]);

  const ActionButtons = (params) => {
    const [deleting, setDeleting] = useState(false);

    const deleteBookingAccount = () => {
      setDeleting(true)
      axiosInstance.post(`Vendor/DeleteBookingVendorAccount?id=${params.row.id}`)
        .then(() => {
          enqueueSnackbar('Booking account deleted successfully', { variant: 'success' });
          setAccounts(accounts => accounts.filter(account => account.id !== params.row.id))
        }).catch(() => enqueueSnackbar('Error deleting booking account', { variant: 'error' }))
        .finally(() => setDeleting(false))
    };

    return <>
      <EditIconButton onClick={() => { setOpenForm(true); setEditAccount(params.row); }} />
      <ConfirmDialog
        icon="delete-outline"
        title="Delete"
        color="error"
        content={`Are you sure you want to delete booking account ${params.row.vendor} - ${params.row.account} - ${params.row.bookingVendor}?`}
        actionButton="Delete"
        action={deleteBookingAccount}
        manualLoading={deleting}
      />
    </>
  };

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    width: 100,
    renderCell: ActionButtons
  };

  const customToolbar = useCallback(() => (
    <Grid container direction="row" alignItems="center" spacing={1}>
      <Grid item>
        <Button
          variant="contained"
          sx={{ ml: 1, mr: 1 }}
          size="small"
          color="secondary"
          onClick={() => { setOpenForm(true); setEditAccount(null); }}
          startIcon={<Iconify icon="eva:plus-outline" />}
        >
          Add Booking Account
        </Button>
      </Grid>
      <Grid item>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={() => window.open(`/Api/Vendor/DownloadVendorBookingAccounts?vendorId=${selectedRow?.id}`, '_blank')}
          startIcon={<Iconify icon="file-icons:microsoft-excel" />}
        >
          Download
        </Button>
      </Grid>
    </Grid>
  ), []);

  return <>
    <CustomDataGrid
      gridId="admin-vendor-booking-account-view"
      boxSX={{ height: 'calc(100vh - 280px)', width: "100%" }}
      data={accounts}
      forceReRender={accounts}
      gridColumns={[...BOOKING, actions]}
      sort={[{ field: 'vendor', sort: 'asc' }]}
      CustomLeftToolbar={customToolbar}
      isLoading={isLoading}
      disableSelectionOnClick
    />
    {openForm && <BookingAccountForm setAccounts={setAccounts} editAccount={editAccount} bookingVendors={bookingVendors} vendors={vendors} close={() => { setOpenForm(false); setEditAccount(null); }} />}
  </>;
}
